import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import NumberInput from '../../components/atoms/forms/NumberInput';
import theme from '../../theme';
import validateMagnifier from './validation';

const StyledDiv = styledComponent('div', {
  '& .traffic-tables-container': {
    display: 'flex',
    gap: theme.spacing.bodyPadding,
    flexWrap: 'wrap',
  },
  '& .traffic-table-wrapper': {
    flex: '1',
    minWidth: '300px',
  },
  '& .traffic-table-title': {
    fontWeight: theme.fontWeights.semibold,
    ...theme.typography.body,
    marginBottom: theme.spacing.gapLg,
    padding: theme.spacing.countInputPadding,
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.input,
  },
  '& .traffic-table': {
    width: '100%',
    borderCollapse: 'collapse',
    ...theme.typography.label,
  },
  '& .traffic-table th': {
    background: theme.colors.bgMuted,
    padding: theme.spacing.countInputPadding,
    textAlign: 'left',
    fontWeight: theme.fontWeights.semibold,
    borderBottom: `2px solid ${theme.colors.borderLight}`,
    fontSize: theme.sizes.smallFontSize,
  },
  '& .traffic-table td': {
    padding: `${theme.spacing.labelGap} ${theme.spacing.gapLg}`,
    borderBottom: theme.borders.card,
  },
  '& .traffic-table td.magnifier-cell': {
    textAlign: 'center',
  },
  '& .traffic-table tbody tr:nth-child(odd)': {
    background: theme.colors.bgMuted,
  },
  '& .time-cell': {
    color: theme.colors.textMuted,
    fontSize: theme.sizes.smallFontSize,
  },
  '& .modified': {
    backgroundColor: `${theme.colors.warningBg} !important`,
  },
  '& .error input': {
    borderColor: theme.colors.danger,
  },
  '@media (max-width: 768px)': {
    '& .traffic-tables-container': {
      flexDirection: 'column',
    },
  },
});

// Generate 24 hourly time slots (e.g., "22:00:00" to "22:59:00")
const generateHourlySlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const hourStr = hour.toString().padStart(2, '0');
    slots.push({
      startTime: `${hourStr}:00:00`,
      endTime: `${hourStr}:59:00`,
    });
  }
  return slots;
};

const hourlySlots = generateHourlySlots();

function TrafficConfigTable({
  weekdayData = [],
  weekendData = [],
  originalWeekdayData,
  originalWeekendData,
  onWeekdayChange,
  onWeekendChange,
  disabled,
}) {
  // Get magnifier value for a specific time slot from the data array
  const getMagnifier = (dataArray, startTime) => {
    const found = dataArray.find((item) => item.startTime === startTime);
    return found ? found.magnifier : '';
  };

  // Get original magnifier value for comparison
  const getOriginalMagnifier = (originalArray, startTime) => {
    if (!originalArray) return null;
    const found = originalArray.find((item) => item.startTime === startTime);
    return found ? found.magnifier : null;
  };

  // Check if a field has been modified
  const isModified = (currentArray, originalArray, startTime) => {
    const currentVal = getMagnifier(currentArray, startTime);
    const originalVal = getOriginalMagnifier(originalArray, startTime);
    if (originalVal === null) return currentVal !== '';
    return String(currentVal) !== String(originalVal);
  };

  // Sanitize input to max 2 decimal places
  const sanitizeDecimalInput = (value) => {
    if (value === '' || value === undefined || value === null) return value;
    // Allow the user to type a decimal point
    if (value === '.' || value.endsWith('.')) return value;
    // If there's a decimal, truncate to max 2 decimal places
    const parts = String(value).split('.');
    if (parts.length === 2 && parts[1].length > 2) {
      return `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    return value;
  };

  // Handle magnifier change
  const handleMagnifierChange = (dayType, startTime, endTime, value) => {
    const sanitizedValue = sanitizeDecimalInput(value);
    const onChange = dayType === 'weekday' ? onWeekdayChange : onWeekendChange;
    const currentData = dayType === 'weekday' ? weekdayData : weekendData;

    // Find existing entry or create new one
    const existingIndex = currentData.findIndex(
      (item) => item.startTime === startTime,
    );

    let newData;
    if (sanitizedValue === '' || sanitizedValue === undefined) {
      // Remove entry if magnifier is cleared
      if (existingIndex >= 0) {
        newData = currentData.filter((_, i) => i !== existingIndex);
      } else {
        newData = [...currentData];
      }
    } else if (existingIndex >= 0) {
      // Update existing entry
      newData = currentData.map((item, i) =>
        i === existingIndex ? { ...item, magnifier: sanitizedValue } : item,
      );
    } else {
      // Add new entry
      newData = [
        ...currentData,
        { startTime, endTime, magnifier: sanitizedValue },
      ];
    }

    onChange(newData);
  };

  const renderTable = (title, dataArray, originalArray, dayType) => (
    <div className="traffic-table-wrapper">
      <div className="traffic-table-title">{title}</div>
      <table className="traffic-table">
        <thead>
          <tr>
            <th style={{ width: '30%' }}>Traffic Window Start</th>
            <th style={{ width: '30%' }}>Traffic Window End</th>
            <th style={{ width: '40%' }}>Magnifier Value</th>
          </tr>
        </thead>
        <tbody>
          {hourlySlots.map((slot) => {
            const magnifier = getMagnifier(dataArray, slot.startTime);
            const modified = isModified(
              dataArray,
              originalArray,
              slot.startTime,
            );
            const magnifierError = !validateMagnifier(magnifier).valid;

            return (
              <tr
                key={`${dayType}-${slot.startTime}`}
                className={modified ? 'modified' : ''}
              >
                <td className="time-cell">{slot.startTime}</td>
                <td className="time-cell">{slot.endTime}</td>
                <td
                  className={`magnifier-cell${magnifierError ? ' error' : ''}`}
                >
                  <NumberInput
                    step="0.01"
                    min="1"
                    value={magnifier}
                    onChange={(e) =>
                      handleMagnifierChange(
                        dayType,
                        slot.startTime,
                        slot.endTime,
                        e.target.value,
                      )
                    }
                    disabled={disabled}
                    placeholder="1.00"
                    aria-label={`${title} ${slot.startTime} magnifier`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <StyledDiv>
      <div className="traffic-tables-container">
        {renderTable('Weekdays', weekdayData, originalWeekdayData, 'weekday')}
        {renderTable('Weekends', weekendData, originalWeekendData, 'weekend')}
      </div>
    </StyledDiv>
  );
}

TrafficConfigTable.propTypes = {
  weekdayData: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.string.isRequired,
      endTime: PropTypes.string.isRequired,
      magnifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ),
  weekendData: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.string.isRequired,
      endTime: PropTypes.string.isRequired,
      magnifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ),
  originalWeekdayData: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
      magnifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  originalWeekendData: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
      magnifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  onWeekdayChange: PropTypes.func.isRequired,
  onWeekendChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

TrafficConfigTable.defaultProps = {
  weekdayData: [],
  weekendData: [],
  originalWeekdayData: null,
  originalWeekendData: null,
  disabled: false,
};

export default TrafficConfigTable;
